// ── Resource surfaces (Wave 8 — REBUILT as gold-pattern pages) ──────────────
// The thin mock-only resource surface is replaced by real pages: ResourcesPage
// (the scope-filtered library with category/scope filters + STAFF create) and
// ResourceDetailPage (the resource viewer with the STAFF-only manage panel, the
// 503 gate-closed file-storage + download honest states, and the reference-video
// view-only restriction for coaches). Each is wired via `customBody` so the route
// registration + provenance metadata is preserved.
//
// PARTICIPANT EXCLUSION (Wave 8 release gate): participant is denied the admin
// module at the route (resourceModuleRoles) AND every page fail-closes. Manage is
// STAFF-ONLY (manage.resources). Resource uploads/downloads are the 503 gate-
// closed honest state (R2 closed). Reference videos are view-only for coaches.

import type { ProductScreenContract } from "./product-route-contracts";
import { ResourcesPage } from "./resources/resources-page";
import { ResourceDetailPage } from "./resources/resource-detail-page";

const RESOURCES_OWNER = "VIL-20260608-0023";

function focusedDefaults(): Pick<
  ProductScreenContract,
  "requiredViewports" | "surfaceMode" | "closureState"
> {
  return {
    requiredViewports: ["desktop", "mobile"],
    surfaceMode: "focused",
    closureState: "focused"
  };
}

export function resourceLibraryScreen(): ProductScreenContract {
  return {
    title: () => "Resources",
    primaryJob:
      "Browse the role-scoped resource library across the three categories (reference videos / documentation / program resources); manage controls stay STAFF-only.",
    secondaryJob:
      "Filter by category/scope; participants are denied the admin module entirely (route + page fail-closed).",
    workTitle: "Resource library",
    rows: [{ id: "resource-1", resource: "Coaching primer", category: "Documentation", scope: "Global" }],
    columns: [
      { key: "resource", header: "Resource" },
      { key: "category", header: "Category" },
      { key: "scope", header: "Scope" }
    ],
    states: ["loading", "empty", "permission-denied", "unavailable", "error"],
    apiFamilies: ["resource-service"],
    roleDeltas:
      "STAFF create/edit/archive + attach; coach/company_admin/cohort_lead scoped VIEW (reference videos viewable but NOT downloadable for coach); PARTICIPANT DENIED the admin module (fail-closed).",
    extensionSlots: ["resource upload (R2 gate-closed)", "resource archive (no endpoint)", "signed download audit", "attach-to-program"],
    mobilePosture: "Filter toolbar, count, create control, and the resources table stack with no horizontal overflow.",
    evidenceRequirement:
      "Playwright must prove the scoped read, STAFF-only manage, participant-denied fail-closed, every honest posture, and the row → detail link.",
    vjalComponents: ["Badge","Button","CollectionToolbar","IconDoc","PageFrame","ProTable","StatusDot"],
    localCompositions: ["resources focused product surface"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "Filter toolbar + the scope-filtered resources table.",
      primaryAction: "Open a resource for its link, manage controls, and download.",
      secondaryPanel: "Category/scope filters restricted to present categories; STAFF create control.",
      deferredSurface: "Resource description, manage, file-storage, and download live on the detail.",
      gatedControls: "Participant denied the module; manage STAFF-only; uploads/downloads gate-closed (R2)."
    },
    ...focusedDefaults(),
    closureOwner: RESOURCES_OWNER,
    customBody: ResourcesPage
  } as ProductScreenContract;
}

export function resourceDetailScreen(): ProductScreenContract {
  return {
    title: () => "Resource detail",
    primaryJob:
      "Open one role-scoped resource with its link, STAFF-only manage (edit/attach + archive not-configured), and the 503 gate-closed file-storage + download honest states.",
    secondaryJob:
      "Reference videos are view-only for coaches (no download affordance). Participant is denied; out-of-scope resources resolve generic-not-found.",
    workTitle: "Resource viewer",
    rows: [{ id: "resource-1", section: "Manage", status: "staff-only" }],
    columns: [
      { key: "section", header: "Section" },
      { key: "status", header: "Status" }
    ],
    states: ["loading", "empty", "permission-denied", "unavailable", "gate-closed", "not-configured", "error"],
    apiFamilies: ["resource-service"],
    roleDeltas:
      "STAFF edit/attach + archive(not-configured); coach/company_admin/cohort_lead VIEW (coach NO reference-video download); uploads/downloads gate-closed (R2); participant DENIED.",
    extensionSlots: ["resource upload (R2 gate-closed)", "resource archive (no endpoint)", "signed download", "attach-to-program"],
    mobilePosture: "Header, description, manage/file-storage/download panels stack with no overflow.",
    evidenceRequirement:
      "Playwright must prove the scoped read, STAFF-only manage, the 503 gate-closed upload + download, the reference-video view-only restriction, and participant denial.",
    vjalComponents: ["Alert","Badge","Button","DetailSurface","Module","ModuleBody","ModuleHead"],
    localCompositions: ["resource-detail focused product surface"],
    packageGapRefs: [],
    decisionHierarchy: {
      firstViewport: "Resource identity header + category/scope/status badges.",
      primaryAction: "Open the resource link; STAFF edit/attach per role.",
      secondaryPanel: "File storage (gate-closed) + download (role/category-gated, gate-closed).",
      deferredSurface: "File upload/download land with the R2 lane; archive lands with its endpoint.",
      gatedControls: "Manage STAFF-only; archive not-configured; coach no reference-video download; participant denied."
    },
    ...focusedDefaults(),
    closureOwner: RESOURCES_OWNER,
    customBody: ResourceDetailPage
  } as ProductScreenContract;
}
